import { useMemo } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { Pencil } from "lucide-react";

import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { AuthorizationRole } from "../../types/authorizationAdmin";
import { Button } from "../atoms/Button";

type DecisionRolesTableProps = {
  readonly roles: AuthorizationRole[];
  /** When true, omit the edit column (decision roles shown read-only). */
  readonly hideActions?: boolean;
  readonly onEdit?: (role: AuthorizationRole) => void;
};

type Row = AuthorizationRole & { readonly actions: null };

export const DecisionRolesTable = ({ roles, hideActions = false, onEdit }: DecisionRolesTableProps) => {
  const rows = useMemo<Row[]>(
    () => roles.map((r) => ({ ...r, actions: null })),
    [roles],
  );

  const columns: TableColumn<Row>[] = useMemo(() => {
    const base: TableColumn<Row>[] = [
      { name: "Code", selector: (row) => row.code, width: "100px" },
      { name: "Name", selector: (row) => row.name, grow: 2 },
      { name: "Priority", selector: (row) => String(row.priority), width: "100px" },
      { name: "All BU", selector: (row) => (row.can_view_all_bu ? "Yes" : "No"), width: "90px" },
      { name: "Status", selector: (row) => (row.is_active ? "Active" : "Inactive"), width: "100px" },
    ];
    if (hideActions || !onEdit) {
      return base;
    }
    return [
      ...base,
      {
        name: "",
        width: "100px",
        cell: (row: Row) => (
          <Button variant="ghost" className="!px-2" onClick={() => onEdit(row)} aria-label="Edit role">
            <Pencil className="h-4 w-4" />
          </Button>
        ),
        ignoreRowClick: true,
      },
    ];
  }, [hideActions, onEdit]);

  return (
    <DataTable
      columns={columns}
      data={rows}
      customStyles={dataTableCustomStyles}
      keyField="id"
      noDataComponent={<span className="py-6 text-sm text-gray-500">No decision roles loaded.</span>}
    />
  );
};
